// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Payments LIST page (Stage 6P.7).
//
// Server-paginated list at /erp/supplier-payments.
//
// Columns:
//   Payment No · Payment Date · Supplier (+ GSTIN tag) · Payment Method
//   Payment Account · Payment Amount · Allocated · Unallocated
//   Status · Posting Status · Voucher No · Actions (⋯)
//
// Toolbar:
//   Search (q over payment_no / supplier / reference)
//   Status filter · Payment-method filter · Posting-status filter
//   Date range (payment_date) · Clear · Refresh
//
// Per-row actions:
//   • View   → navigate to /erp/supplier-payments/:id
//   • Post   → only available when status ∈ {DRAFT, POSTING_FAILED}
//              (backend posts the voucher via voucherPosting.service)
//   • Cancel → only available when status ∈ {POSTED, POSTING_FAILED}
//              (backend reverses ledger impact + sets cancelled_at)
//
// Backend remains the authority for ALL payment math:
//   ledger code resolution (payment_account + supplier_payable chain),
//   payable balance computation, debit/credit balance enforcement,
//   duplicate-block decisions, voucherPosting integration.
// This page is a pure read + dispatch surface.
//
// Style:
//   Mirrors PurchaseAccountingPostingsPage — slate-50 header strip,
//   22 px row density, sticky toolbar, ⋯ row-actions menu. Reuses the
//   shared GoodsReceiptsPage.css via the .grn-* wrapper so no new CSS
//   file is added.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Dropdown, Input, Modal, Select,
  Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined,
  ExclamationCircleOutlined, EyeOutlined, StopOutlined,
  CheckCircleOutlined, BankOutlined
} from '@ant-design/icons';

import { supplierPaymentApi } from './supplierPaymentApi';
import {
  STATUSES,
  POSTING_STATUSES,
  PAYMENT_METHODS,
  paymentStatusLabel,
  paymentStatusColor,
  postingStatusLabel,
  postingStatusColor,
  paymentMethodLabel,
  canPostSupplierPayment,
  canCancelSupplierPayment
} from './supplierPaymentHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const STATUS_OPTIONS = STATUSES.map((v) => ({
  value: v,
  label: paymentStatusLabel(v)
}));

const POSTING_STATUS_OPTIONS = POSTING_STATUSES.map((v) => ({
  value: v,
  label: postingStatusLabel(v)
}));

const PAYMENT_METHOD_OPTIONS = PAYMENT_METHODS.map((v) => ({
  value: v,
  label: paymentMethodLabel(v)
}));


// ─────────────────────────────────────────────────────────────────
// Pure helpers — kept local so the page doesn't depend on a shared
// formatter module that could drift.
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// ₹ grouping is locale-aware (en-IN) so 12,34,567.89 is rendered the
// same way as elsewhere on the control plane. Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Compose the unallocated amount = payment_amount − allocated_amount.
// The backend already emits unallocated_amount; we prefer it when
// present and fall back to a derived value so a tenant that returns
// just the parts still renders a sensible number.
function readUnallocated(row) {
  if (!row) return null;
  const direct = pickField(row, 'unallocated_amount', 'unallocatedAmount');
  if (direct !== undefined && direct !== null && direct !== '') {
    const n = Number(direct);
    if (Number.isFinite(n)) return n;
  }
  const paid = Number(pickField(row, 'payment_amount',   'paymentAmount')   || 0);
  const alc  = Number(pickField(row, 'allocated_amount', 'allocatedAmount') || 0);
  if (!Number.isFinite(paid) && !Number.isFinite(alc)) return null;
  return (Number.isFinite(paid) ? paid : 0) - (Number.isFinite(alc) ? alc : 0);
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function SupplierPaymentsPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.supplier_payments.*` strings are the Stage 6P.7 perms.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView   = hasPerm('erp.supplier_payments.view');
  const canPost   = hasPerm('erp.supplier_payments.post');
  const canCancel = hasPerm('erp.supplier_payments.cancel');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput,   setSearchInput]   = useState('');
  const [search,        setSearch]        = useState('');
  const [status,        setStatus]        = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [postingStatus, setPostingStatus] = useState(null);
  const [dateRange,     setDateRange]     = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the accounting list.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      // Send under BOTH `q` and `search` — backend tolerates the
      // unused one via stripUnknown:true on its Joi validator.
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      if (status)        params.status        = status;
      if (paymentMethod) params.paymentMethod = paymentMethod;
      if (postingStatus) params.postingStatus = postingStatus;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await supplierPaymentApi.listPayments(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load supplier payments.'),
        key: 'sp-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, status, paymentMethod, postingStatus, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onStatus        = (v) => { setStatus(v || null);        setPage(1); };
  const onPaymentMethod = (v) => { setPaymentMethod(v || null); setPage(1); };
  const onPostingStatus = (v) => { setPostingStatus(v || null); setPage(1); };
  const onDates         = (v) => { setDateRange(v || null);     setPage(1); };
  const onReset = () => {
    setSearchInput('');
    setSearch('');
    setStatus(null);
    setPaymentMethod(null);
    setPostingStatus(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  const openView = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/supplier-payments/${encodeURIComponent(id)}`);
  }, [navigate]);

  // ── Post handler — confirmed via Modal with an optional remarks
  // prompt. Backend recomputes ledger lines + posts via
  // voucherPosting.service. UI never wins.
  const runPost = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let remarks = '';
    Modal.confirm({
      title: 'Post supplier payment?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This will post payment{' '}
            <strong>{pickField(row, 'payment_no', 'paymentNo') || id}</strong>{' '}
            to the general ledger via the payment voucher. Provide a short
            note (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Remarks (optional)"
            onChange={(e) => { remarks = e.target.value; }}
          />
        </div>
      ),
      okText: 'Post Payment',
      cancelText: 'Cancel',
      async onOk() {
        setBusyId(id);
        try {
          const payload = remarks && remarks.trim() ? { remarks: remarks.trim() } : {};
          await supplierPaymentApi.postPayment(id, payload);
          message.success('Payment posted.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to post payment.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Cancel handler — confirmed via Modal with a reason prompt ─
  const runCancel = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel supplier payment?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This will reverse the ledger impact of payment{' '}
            <strong>{pickField(row, 'payment_no', 'paymentNo') || id}</strong>{' '}
            and mark it cancelled. Provide a short reason (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Reason (optional)"
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText: 'Cancel Payment',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        setBusyId(id);
        try {
          await supplierPaymentApi.cancelPayment(id, reason || undefined);
          message.success('Payment cancelled.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to cancel payment.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    items.push({
      key:   'view',
      icon:  <EyeOutlined />,
      label: 'View'
    });

    const paymentStatus = pickField(row, 'status', 'status');
    if (canPost && canPostSupplierPayment(paymentStatus)) {
      items.push({ type: 'divider' });
      items.push({
        key:   'post',
        icon:  <CheckCircleOutlined />,
        label: 'Post'
      });
    }
    if (canCancel && canCancelSupplierPayment(paymentStatus)) {
      items.push({ type: 'divider' });
      items.push({
        key:    'cancel',
        icon:   <StopOutlined />,
        label:  'Cancel',
        danger: true
      });
    }
    return items;
  }, [canPost, canCancel]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'view':   return openView(row);
      case 'post':   return runPost(row);
      case 'cancel': return runCancel(row);
      default:       return undefined;
    }
  }, [openView, runPost, runCancel]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Payment No',
      dataIndex: 'paymentNo',
      width:     170,
      render: (_v, row) => {
        const no = pickField(row, 'payment_no', 'paymentNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openView(row)}
          >
            {no}
          </button>
        );
      }
    },
    {
      title:     'Payment Date',
      dataIndex: 'paymentDate',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'payment_date', 'paymentDate'))}
        </span>
      )
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Payment Method',
      dataIndex: 'paymentMethod',
      width:     140,
      render: (_v, row) => {
        const m = pickField(row, 'payment_method', 'paymentMethod');
        if (!m) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag" color="default">
            {paymentMethodLabel(m) || String(m).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Payment Account',
      dataIndex: 'paymentAccountName',
      width:     180,
      render: (_v, row) => {
        const name = pickField(row, 'payment_account_name', 'paymentAccountName');
        const code = pickField(row, 'payment_account_code', 'paymentAccountCode');
        if (!name && !code) return <span className="grn-muted">—</span>;
        return (
          <Tooltip title={code ? `${name || ''} (${code})` : name || ''}>
            <span className="grn-supplier__name">{name || code || '—'}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'Payment Amount',
      dataIndex: 'paymentAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => {
        const amt = pickField(row, 'payment_amount', 'paymentAmount');
        return (
          <span className="grn-mono grn-line-count">{fmtMoney(amt)}</span>
        );
      }
    },
    {
      title:     'Allocated',
      dataIndex: 'allocatedAmount',
      width:     130,
      align:     'right',
      render: (_v, row) => {
        const amt = pickField(row, 'allocated_amount', 'allocatedAmount');
        return (
          <span className="grn-mono grn-line-count">{fmtMoney(amt)}</span>
        );
      }
    },
    {
      title:     'Unallocated',
      dataIndex: 'unallocatedAmount',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(readUnallocated(row))}
        </span>
      )
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     130,
      render: (_v, row) => {
        const v = pickField(row, 'status', 'status');
        if (!v) return (
          <Tag className="grn-status-tag" color="default">Draft</Tag>
        );
        return (
          <Tag color={paymentStatusColor(v)} className="grn-status-tag">
            {paymentStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Posting Status',
      dataIndex: 'postingStatus',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'posting_status', 'postingStatus');
        if (!v) return (
          <Tag className="grn-status-tag" color="default">Not Posted</Tag>
        );
        return (
          <Tag color={postingStatusColor(v)} className="grn-status-tag">
            {postingStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Voucher No',
      dataIndex: 'voucherNo',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'voucher_no', 'voucherNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = pickField(row, 'id', 'id');
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [openView, buildRowMenu, onRowMenu, busyId]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view supplier payments."
          description="Ask your master account to grant erp.supplier_payments.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><BankOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Supplier Payments</span>
            <span className="grn-headstrip__subtitle">
              Record and post supplier payments — bill-wise, advances, or
              on-account. Backend owns ledger resolution, debit/credit
              balance, payable balance computation, and duplicate-block
              decisions — this surface reads and dispatches post / cancel
              actions only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search payment no, supplier, reference…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            // Flush debounce on Enter.
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 300 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All statuses"
          value={status || undefined}
          onChange={onStatus}
          options={STATUS_OPTIONS}
          style={{ width: 160 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All methods"
          value={paymentMethod || undefined}
          onChange={onPaymentMethod}
          options={PAYMENT_METHOD_OPTIONS}
          style={{ width: 170 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All posting statuses"
          value={postingStatus || undefined}
          onChange={onPostingStatus}
          options={POSTING_STATUS_OPTIONS}
          style={{ width: 200 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || pickField(r, 'payment_no', 'paymentNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openView(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No supplier payments yet.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
